import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { mapStateToProps, mapDispatchToProps } from '../../redux/reduxActions';
import { FpcModel, DrawerItemsType } from '../../models/models';
import { FpcLib } from '../../models/fpcModule';
import SelectCQ from './SelectCQ';
import Params from './Params';
import Confirm from './Confirm';
import ResultsGrid from './ResultsGrid';
import Results from './Results';
import ReportGen from './ReportGen';
import Error from './Error';
import { PageViewTypeEnum, fpcTypeEnum, CQStatusEnum, StepItemIndexEnum } from '../../models/enums';
import { addPageViewClass } from '../../functions/generalFunctions'

import { Stepper, StepperChangeEvent } from '@progress/kendo-react-layout';


import './FpcCtl.css';
import { TcbObjInfo } from '../../models/models';

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

type FpcCtlProps = PropsFromRedux & {
  menuItem: DrawerItemsType;
  clientQueryGrpId?: number;
  clientQueryId?: number;
  tcbObjs?: TcbObjInfo[];
  securityId?: number;
  stateToLoad?: FpcCtlState;
  onSaveState?: (menuItemId: number, itemState: any, callback?: () => void) => void;
  onSaveMenuItem?: (menuItemId: number, itemState: any, menuCustomTxt: string, callback?: () => void) => void;
  onStartSSRSReport?: (fpc: FpcModel, menuItemId: number) => void;
}

interface FpcCtlState {
  fpc: FpcModel;
  isLoading: boolean;
  pageStyle: React.CSSProperties;
}

const getRedirectUri = () => {
  return process.env.PUBLIC_URL;

};

class FpcCtl extends React.Component<FpcCtlProps, FpcCtlState> {
  private _elapsedTimerId: ReturnType<typeof setInterval> | null = null;
  private _isRestoredState: boolean = false;
  private _isUnmounted: boolean = false;

  componentDidUpdate(prevProps: FpcCtlProps, prevState: FpcCtlState) {
    // Detect transition to ssrsReportRunning true
    if (
      !prevState.fpc.ssrsReportRunning &&
      this.state.fpc.ssrsReportRunning
    ) {
      // Notify App.tsx to start SSRS orchestration and close this page
      if (this.props.onStartSSRSReport) {
        this.props.onStartSSRSReport(this.state.fpc, this.props.menuItem.menuitemid);
      }
    }

    if (
      prevState.isLoading !== this.state.isLoading ||
      prevState.fpc.cqRunStartedOnUtcMs !== this.state.fpc.cqRunStartedOnUtcMs
    ) {
      this.syncElapsedTimer();
    }
  }

  constructor(props: FpcCtlProps) {
    super(props);

    if (this.props.stateToLoad) {
      this._isRestoredState = true;
      // Fix: Ensure SSRS report state is restored and parameters/results are not shown
      let restoredState = { ...this.props.stateToLoad };
      if (restoredState.fpc) {
        // If SSRS report result file is present, always show completed state
        if (restoredState.fpc.fpcType === fpcTypeEnum.ReportViewer && restoredState.fpc.ResultData && restoredState.fpc.ResultData.ClientQueryResultId) {
          restoredState.fpc.ssrsReportRunning = false;
          restoredState.fpc.ssrsReportComplete = true;
          restoredState.isLoading = false;
        } else if (restoredState.fpc.fpcStatus === CQStatusEnum.ReportGen) {
          restoredState.fpc.ssrsReportRunning = true;
          restoredState.fpc.ssrsReportComplete = false;
          restoredState.isLoading = true;
        } else if (
          restoredState.fpc.fpcType === fpcTypeEnum.ReportViewer &&
          [CQStatusEnum.DisplayCqResults, CQStatusEnum.DisplayCqResultsGrid, CQStatusEnum.DisplayError].includes(restoredState.fpc.fpcStatus)
        ) {
          restoredState.fpc.ssrsReportRunning = false;
          restoredState.fpc.ssrsReportComplete = true;
        }
      }
      this.state = restoredState;
    } else {
      let fpc = new FpcModel();
      fpc.userInfo = this.props.userInf;
      fpc.fpcStatus = CQStatusEnum.DisplayError;
      fpc.errorMessage = "No Group or Client Query supplied";

      fpc.clientQueryGrpId = this.props.clientQueryGrpId;
      if (fpc.clientQueryGrpId) {
        fpc.fpcStatus = CQStatusEnum.SelectCQ;
        fpc.canSelectQuery = true;
        switch (fpc.clientQueryGrpId) {
          case 1:
            fpc.cqHeader = "Admin";
            break;
          case 2:
            fpc.cqHeader = "Report"
            break;
          case 3:
          case 4:
            fpc.cqHeader = "Action"
            break;
        }
      }
      if (this.props.clientQueryId) {
        fpc.fpcStatus = CQStatusEnum.LoadCQ;
        fpc.canSelectQuery = false;
        fpc.clientQueryId = this.props.clientQueryId;
      }
      if (this.props.tcbObjs) {
        fpc.clientQueryTcbObjs = this.props.tcbObjs
        fpc.clientQueryParamValues = this._fpcLib.BuildParamValsFromTcbObjs(fpc.clientQueryTcbObjs, this.props.securityId);
      }

      this.state = {
        fpc: fpc,
        isLoading: true,
        pageStyle: {}
      }
    }
  }

  private _fpcLib: FpcLib = new FpcLib();
  private _updateMenuItem: boolean = false;

  componentDidMount() {
    this._isUnmounted = false;
    this.updateDimensions();
    let fpc = this.state.fpc;

    // When restoring from saved menu item state, keep that state instead of reloading
    // the CQ definition, which can reset the flow back to Select CQ.
    if (this._isRestoredState) {
      if (fpc.stepperItems && fpc.stepperItems.length > 0) {
        this._fpcLib.SetCurrentStepperItem(fpc, fpc.stepperCurrentIndex);
      }

      // If a CQ async job is still in progress, resume polling after restore.
      if (this.state.isLoading && !!fpc.cqRunJobId) {
        this._fpcLib.ResumeRunCqPolling(fpc, this.runCqComplete, (updatedFpc) => {
          this.setState({ fpc: updatedFpc }, () => this.saveMenuItem());
        });
        this.syncElapsedTimer();
        return;
      }

      this.setState({ fpc: fpc, isLoading: this.state.isLoading }, this.saveMenuItem);
      this.syncElapsedTimer();
      return;
    }

    // Fix: If restoring to ReportGen, ensure correct rendering
    if (fpc.fpcStatus === (CQStatusEnum as any).ReportGen) {
      this._fpcLib.SetCurrentStepperItem(fpc, StepItemIndexEnum.Results);
      this.setState({ fpc: fpc, isLoading: false });
      return;
    }
    switch (fpc.fpcStatus) {
      case CQStatusEnum.SelectCQ:   //Setup page for Selection of CQ
        if (fpc.canSelectQuery) {
          this._fpcLib.LoadStepperItems(fpc, this.gotoToSelect, this.validateError);
        }
        break;

      case CQStatusEnum.DisplayCqResults:
      case CQStatusEnum.DisplayCqResultsGrid:
      case CQStatusEnum.DisplayError:
      case CQStatusEnum.ReportGen:
        this.displayResults();
        break;

      default:
        this.loadSelectedCQ(fpc);
        break;
    }

    this.syncElapsedTimer();
  }

  componentWillUnmount() {
    this._isUnmounted = true;
    this.saveMenuItem();
    this.stopElapsedTimer();
  }

  private persistFpcState = (fpc: FpcModel, isLoading: boolean, callback?: () => void) => {
    if (!this._isUnmounted) {
      this.setState({ fpc: fpc, isLoading: isLoading }, () => this.saveMenuItem(callback));
      return;
    }

    const itemState = { ...this.state, fpc: fpc, isLoading: isLoading };
    if (!this.props.menuItem.fixedItem) {
      if (this.props.onSaveMenuItem) {
        this.props.onSaveMenuItem(this.props.menuItem.menuitemid, itemState, fpc.clientQueryDesc, callback);
      } else if (callback) {
        callback();
      }
    } else {
      if (this.props.onSaveState) {
        this.props.onSaveState(this.props.menuItem.menuitemid, itemState, callback);
      } else if (callback) {
        callback();
      }
    }
  }

  updateDimensions = () => {
    let body = document.body;
    if (body) {
        let ps: React.CSSProperties = { ...this.state.pageStyle };
        if (this.props.pageInf.pageViewMode === PageViewTypeEnum.Mobile) {
            ps.width = body.clientWidth;
        }
        this.setState({
            pageStyle: ps
        });
    }
}

  private getElapsedLoadingMessage = (fpc: FpcModel): string => {
    const baseMessage = fpc.cqRunBaseLoadingStatusMsg && fpc.cqRunBaseLoadingStatusMsg.trim().length > 0
      ? fpc.cqRunBaseLoadingStatusMsg
      : (fpc.clientQueryDesc && fpc.clientQueryDesc.trim().length > 0 ? fpc.clientQueryDesc + "..." : "Running Process...");

    const elapsedSeconds = fpc.cqRunStartedOnUtcMs > 0
      ? Math.max(fpc.cqRunElapsedSeconds, Math.floor((Date.now() - fpc.cqRunStartedOnUtcMs) / 1000))
      : fpc.cqRunElapsedSeconds;

    return baseMessage + ' (Running - ' + elapsedSeconds.toString() + 's elapsed)';
  }

  private stopElapsedTimer = () => {
    if (this._elapsedTimerId) {
      clearInterval(this._elapsedTimerId);
      this._elapsedTimerId = null;
    }
  }

  private syncElapsedTimer = () => {
    const fpc = this.state.fpc;
    const shouldRun = this.state.isLoading && fpc.cqRunStartedOnUtcMs > 0;

    if (!shouldRun) {
      this.stopElapsedTimer();
      return;
    }

    const updateElapsed = () => {
      const fpcNow = this.state.fpc;
      const nextElapsed = Math.max(fpcNow.cqRunElapsedSeconds, Math.floor((Date.now() - fpcNow.cqRunStartedOnUtcMs) / 1000));
      const nextMsg = this.getElapsedLoadingMessage(fpcNow);

      if (nextElapsed !== fpcNow.cqRunElapsedSeconds || nextMsg !== fpcNow.loadingStatusMsg) {
        fpcNow.cqRunElapsedSeconds = nextElapsed;
        fpcNow.loadingStatusMsg = nextMsg;
        this.setState({ fpc: fpcNow }, () => this.saveMenuItem());
      }
    };

    updateElapsed();

    if (!this._elapsedTimerId) {
      this._elapsedTimerId = setInterval(updateElapsed, 1000);
    }
  }

  // saveState = (fpc: FpcModel, isLoading: boolean, callback?: () => void) => {
  //   this.setState({ fpc: fpc, isLoading: isLoading }, () => {
  //     if (fpc.canSelectQuery) {
  //       if (this.props.onSaveState)
  //         this.props.onSaveState(this.props.menuItemId, this.state, callback);
  //     } else {
  //       if (this.props.onSaveMenuItem)
  //         this.props.onSaveMenuItem(this.props.menuItemId, this.state, fpc.clientQueryDesc, callback);
  //     }
  //   });
  // }

  validateError = (r: FpcModel) => {
    this.setState({ isLoading: false, fpc: r }, this.saveMenuItem);
  }

  updateFpcModel = (r: FpcModel) => {
    this.setState({
      fpc: r
    }, this.saveMenuItem);
  }

  // backToSelectCQ = () => {
  //   let fpc = this.state.fpc;
  //   fpc.fpcStatus = CQStatusEnum.SelectCQ;
  //   this.setState({ isLoading: false, fpc: fpc });
  // }


  loadSelectedCQ = (r: FpcModel) => {
    this._fpcLib.LoadStepperItems(r, () => {
      this._fpcLib.LoadClientQuery(r, () => {
        if (!this.props.menuItem.fixedItem) this._updateMenuItem = true;
        this._fpcLib.UpdateStepperStatusWithRequiredParamsStatus(r);

        let currItm = r.stepperItems.find(x => x.current);
        if (currItm) {
          switch (currItm.index) {
            case StepItemIndexEnum.Select:
              this.gotoToSelect();
              break;
            case StepItemIndexEnum.Parameters:
              this.gotoToParams();
              break;
            case StepItemIndexEnum.Validate:
              this.gotoToValidate();
              break;
            default:
              this.gotoToResults();
              break;
          }
        }
        // this.setState({ isLoading: false, fpc: r });

      }, this.validateError);
    }, this.validateError);
  }

  saveMenuItem = (callback?: () => void) => {
    let fpc = this.state.fpc;
    if (!this.props.menuItem.fixedItem) {
      if (this.props.onSaveMenuItem) {
        this.props.onSaveMenuItem(this.props.menuItem.menuitemid, this.state, fpc.clientQueryDesc, callback);
      } else if (callback) {
        callback();
      }
      this._updateMenuItem = false;
    } else {
      if (this.props.onSaveState) {
        this.props.onSaveState(this.props.menuItem.menuitemid, this.state);
      }
      if (callback) {
        callback();
      }
    }
  }

  gotoToSelect = () => {
    let fpc = this.state.fpc;

    if (fpc.canSelectQuery) {
      this._fpcLib.SetCurrentStepperItem(fpc, 0);
      fpc.fpcStatus = CQStatusEnum.SelectCQ;

      this.setState({ fpc: fpc, isLoading: false }, this.saveMenuItem);
    }
  }

  gotoToParams = () => {
    let fpc = this.state.fpc;
    fpc.fpcStatus = CQStatusEnum.DisplayParams;
    this._fpcLib.SetCurrentStepperItem(fpc, StepItemIndexEnum.Parameters);
    this.setState({ fpc: fpc, isLoading: false }, this.saveMenuItem);
  }

  gotoToValidate = () => {
    let fpc = this.state.fpc;

    if (fpc.clientQueryId !== 0) {
      this._fpcLib.SetCurrentStepperItem(fpc, StepItemIndexEnum.Validate);
      fpc.loadingStatusMsg = "Running Validation...";
      this.setState({ fpc: fpc }, () => {
        this.saveMenuItem();
        this._fpcLib.RunCqValidation(fpc, () => {
          this.runCqValidationComplete(fpc);
        });
      });
    }
  }

  gotoToResults = () => {
    let fpc = this.state.fpc;

    if (fpc.clientQueryId !== 0) {
      this._fpcLib.SetCurrentStepperItem(fpc, StepItemIndexEnum.Results);

      // Set the loading message.
      if (fpc.clientQueryDesc) {
        fpc.loadingStatusMsg = fpc.clientQueryDesc + "...";
      }
      else {
        fpc.loadingStatusMsg = "Running Process...";
      }

      this.setState({ fpc: fpc, isLoading: true }, () => {
        this.saveMenuItem();
        this._fpcLib.RunCq(fpc, this.runCqComplete, (updatedFpc) => {
          this.persistFpcState(updatedFpc, true);
        });
      });
      // this.saveState(fpc, true, () => this.state.fpc.RunCq(this.runCqComplete));

    }
  }

  displayResults = () => {
    let fpc = this.state.fpc;
    this._fpcLib.SetCurrentStepperItem(fpc, StepItemIndexEnum.Results);
    this.setState({ fpc: fpc, isLoading: false }, this.saveMenuItem);

  }



  disableStepperItemsAfterError = (r: FpcModel) => {
    r.stepperEnableNextButton = false;
    r.stepperItems.forEach(x => { x.disabled = true });
    if (r.clientQueryParams.some(x => x.isVisible)) {
      let par = r.stepperItems.find(x => x.index === StepItemIndexEnum.Parameters);
      if (par)
        par.disabled = false;
    }

    return r;
  }



  // confirmSubmit = (r: FpcModel) => {
  //   if (r.clientQueryDesc) {
  //     r.loadingStatusMsg = r.clientQueryDesc + "...";
  //   }
  //   else {
  //     r.loadingStatusMsg = "Running Process...";
  //   }

  //   this.setState({ isLoading: true, fpc: r }, () => this._fpcLib.RunCq(r, this.runCqComplete));
  // }


  runCqValidationComplete = (r: FpcModel) => {
    switch (r.fpcStatus) {
      case CQStatusEnum.DisplayError:
        //Got straight to error page
        let r1 = this.disableStepperItemsAfterError(r);
        this.setState({ isLoading: false, fpc: r1 }, () => this.cleanupConnection(false));
        break;

      case CQStatusEnum.DisplayConfirmResults:
        //Load confirm page
        this._fpcLib.UpdateStepperStatusWithValidationStatus(r);
        this._fpcLib.SetCurrentStepperItem(r, StepItemIndexEnum.Validate);
        this.setState({ isLoading: false, fpc: r }, this.saveMenuItem);
        break;

      case CQStatusEnum.RunCq:
        //Validation has returned RUN response - run CQ immediately
        this.gotoToResults();
        break;

      case CQStatusEnum.ReportGen:
        //Run Report: notify App.tsx to start SSRS orchestration
        if (this.props.onStartSSRSReport) {
          this.props.onStartSSRSReport(r, this.props.menuItem.menuitemid);
        }
        break;

    }
  }


  runCqComplete = (r: FpcModel) => {
    console.log('Run CQ Complete');
    if (r.fpcStatus === CQStatusEnum.DisplayError) {
      let r1 = this.disableStepperItemsAfterError(r);
      this.persistFpcState(r1, false);
    } else if (r.fpcType === fpcTypeEnum.ReportViewer) {
      // Immediately notify App.tsx to start SSRS orchestration
      if (this.props.onStartSSRSReport) {
        this.props.onStartSSRSReport(r, this.props.menuItem.menuitemid);
      }
      // Optionally, you can still update local state if needed for UI
      r.loadingStatusMsg = "Running SSRS Report : " + r.clientQueryDesc;
      r.ssrsReportRunning = true;
      r.ssrsReportComplete = false;
      this.persistFpcState(r, true);
    } else {
      if (r.ResultData.ClientQueryResultId === '') {
        this.persistFpcState(r, false);
      } else {
        r.loadingStatusMsg = "Fetching results...  0% completed";
        this.persistFpcState(r, true, () => this.fetchCqData(r, 0, () => this.saveMenuItem()));
      }
    }
  }


  fetchCqData = (r: FpcModel, rowNum: number, callback?: (e: FpcModel) => void) => {
    var retryCount = 0;
    this._fpcLib.FetchCqData(r, rowNum, (r: FpcModel) => {
      //Row Fetched
      rowNum++;
      retryCount = 0;

      if (r.ResultData.ClientQueryResultRowCount > rowNum) {
        r.loadingStatusMsg = "Fetching results... " + Math.floor(rowNum / r.ResultData.ClientQueryResultRowCount * 100).toString() + "% completed";
        this.setState({ isLoading: true, fpc: r }, () => {
          this.saveMenuItem();
          this.fetchCqData(r, rowNum, callback);
        });
      } else {
        r.loadingStatusMsg = "Fetching results... 100% completed";
        this.setState({ isLoading: true, fpc: r }, () => {
          this.saveMenuItem();
          this._fpcLib.ConvertFetchedData(r);
          r.fpcStatus = CQStatusEnum.DisplayCqResultsGrid;
          this.setState({ isLoading: false, fpc: r }, () => { if (callback) callback(r) });
        });
      }
    }, (e) => {
      //Fail
      retryCount++;
      r.loadingStatusMsg = "Fetching results... " + Math.floor(rowNum / r.ResultData.ClientQueryResultRowCount * 100).toString() + "% completed";
      r.loadingStatusMsg += "Failed to fetch : " + retryCount.toString();
      r.loadingStatusMsg += e;
      this.setState({ isLoading: true, fpc: r }, () => {
        this.saveMenuItem();
        this.fetchCqData(r, rowNum);
      });
    });
  }


  // SSRS completion is now handled in App.tsx via onReportDownload callback

  cleanupDownloadCache = (fpc: FpcModel, callback: (e: FpcModel) => void) => {
    this._fpcLib.RemoveCqResultData(fpc, callback);
  }

  cleanupConnection = (addToMenu: boolean) => {
    let fpc = this.state.fpc;
    if (fpc.ResultData.ClientQueryResultId) {
      this._fpcLib.RemoveCqResultData(fpc);
      fpc.ResultData.ClientQueryResultId = '';
      this.setState({ isLoading: false, fpc: fpc });
    }

    if (addToMenu && this.props.onSaveMenuItem) {
      this.props.onSaveMenuItem(this.props.menuItem.menuitemid, this.state, this.state.fpc.clientQueryDesc);
    } else if (this.props.onSaveState) {
      this.props.onSaveState(this.props.menuItem.menuitemid, this.state);
    }
  }

  closePage = () => {

  const { fpc } = this.state;
  // If SSRS report is complete, do NOT add to menu
  if (fpc && fpc.fpcType === fpcTypeEnum.ReportViewer && fpc.ssrsReportComplete) {
    this.cleanupConnection(false); // Do not add to menu
  } else {
    this.cleanupConnection(true); // Add to menu so user can return
  }
  }

  stepperChangePage = (e: StepperChangeEvent) => {
    switch (e.value) {
      case 0: //Select CQ
        this.gotoToSelect();
        break;

      case 1: //Parameters
        this.gotoToParams();
        break;

      case 2: //Validate
        this.gotoToValidate();
        break;

      case 3: //Results
        this.gotoToResults();
        break;

    }
  }

  stepperButtonBack = (e: React.MouseEvent<HTMLButtonElement>) => {
    let fpc = this.state.fpc;

    switch (fpc.stepperCurrentIndex) {

      case 0: //Select CQ

        break;

      case 1: //Parameters
        if (this._fpcLib.IsItemEnabled(fpc, StepItemIndexEnum.Select))
          this.gotoToSelect();
        break;

      case 2: //Validate
        if (this._fpcLib.IsItemEnabled(fpc, StepItemIndexEnum.Parameters))
          this.gotoToParams();
        else if (this._fpcLib.IsItemEnabled(fpc, StepItemIndexEnum.Select))
          this.gotoToSelect();
        break;

      case 3: //Results
        if (this._fpcLib.IsItemEnabled(fpc, StepItemIndexEnum.Validate))
          this.gotoToValidate();
        else if (this._fpcLib.IsItemEnabled(fpc, StepItemIndexEnum.Parameters))
          this.gotoToParams();
        else if (this._fpcLib.IsItemEnabled(fpc, StepItemIndexEnum.Select))
          this.gotoToSelect();



    }

  }

  stepperButtonNext = (e: React.MouseEvent<HTMLButtonElement>) => {
    let fpc = this.state.fpc;

    switch (fpc.stepperCurrentIndex) {

      case 0: //Select CQ
        if (this._fpcLib.IsItemEnabled(fpc, StepItemIndexEnum.Parameters))
          this.gotoToParams();
        else if (this._fpcLib.IsItemEnabled(fpc, StepItemIndexEnum.Validate))
          this.gotoToValidate();
        else if (this._fpcLib.IsItemEnabled(fpc, StepItemIndexEnum.Results))
          this.gotoToResults();

        break;

      case 1: //Parameters
        if (this._fpcLib.IsItemEnabled(fpc, StepItemIndexEnum.Validate))
          this.gotoToValidate();
        else if (this._fpcLib.IsItemEnabled(fpc, StepItemIndexEnum.Results))
          this.gotoToResults();
        break;

      case 2: //Validate
        if (this._fpcLib.IsItemEnabled(fpc, StepItemIndexEnum.Results))
          this.gotoToResults();
        break;

      case 3: //Results
        break;


    }

  }

  AppHeader() {
    return (
      <div key={1} id={"fpcAppHeader"} className= {addPageViewClass(this.props.pageInf)} >
        <div className={"fpcStepperDiv" + addPageViewClass(this.props.pageInf) }>
          <div className="fpcStepperBtnDiv">
            <button onClick={this.stepperButtonBack} disabled={!this.state.fpc.stepperEnableBackButton}><div className="k-icon k-font-icon k-i-arrow-60-left" /></button>
            <div>Back</div>
          </div>
          <Stepper className={"fpcStepper" + addPageViewClass(this.props.pageInf)} items={this.state.fpc.stepperItems} value={this.state.fpc.stepperCurrentIndex} onChange={this.stepperChangePage} />
          <div className="fpcStepperBtnDiv">
            <button onClick={this.stepperButtonNext} disabled={!this.state.fpc.stepperEnableNextButton}><div className="k-icon k-font-icon k-i-arrow-60-right" /></button>
            <div>Next</div>
          </div>
        </div>
        <div className={"fpcReportName" + addPageViewClass(this.props.pageInf)}>{this.state.fpc.clientQueryDesc}</div>
      </div>);
  }


  AppBody() {
    // SSRS report running: always show loading spinner, never show parameters/results
    if (this.state.fpc.ssrsReportRunning) {
      return (
        <div className="fpcLoading">
          <div className="fpcLoadingIndicator"><span className="k-icon k-font-icon k-i-loading" /></div>
          <div className="fpcStatusMsg">{this.state.fpc.loadingStatusMsg}</div>
        </div>
      );
    }
    // SSRS report complete: show ReportGen (PDF download UI)
    if (this.state.fpc.ssrsReportComplete && this.state.fpc.fpcType === fpcTypeEnum.ReportViewer) {
      return <ReportGen model={this.state.fpc} />;
    }
    // ...existing code...
    if (this.state.isLoading) {
      return (
        <div className="fpcLoading">
          <div className="fpcLoadingIndicator"><span className="k-icon k-font-icon k-i-loading" /></div>
          <div className="fpcStatusMsg">{this.state.fpc.loadingStatusMsg}</div>
        </div>
      );
    } else {
      switch (this.state.fpc.fpcStatus) {
        case CQStatusEnum.SelectCQ:
          return <SelectCQ model={this.state.fpc} onSelectChanged={this.loadSelectedCQ} />
        case CQStatusEnum.DisplayParams:
          return <Params model={this.state.fpc} onModelUpdate={this.updateFpcModel} />
        case CQStatusEnum.DisplayConfirmResults:
          return <Confirm model={this.state.fpc} onModelUpdate={this.updateFpcModel} />
        case CQStatusEnum.DisplayCqResultsGrid:
          return <ResultsGrid model={this.state.fpc} />
        case CQStatusEnum.DisplayCqResults:
          return <Results model={this.state.fpc} />
        case CQStatusEnum.ReportGen:
          return <ReportGen model={this.state.fpc} />
        case CQStatusEnum.DisplayError:
          return <Error model={this.state.fpc} />
      };
    }
  }


  render() {

    return (

    <div id="fpcMainDiv" className={addPageViewClass(this.props.pageInf)} style={this.state.pageStyle}>
      {this.AppHeader()}
      <div key={2} className={"appContent" + addPageViewClass(this.props.pageInf)}>{this.AppBody()}</div>
    </div>);
  }
}



export default connector(FpcCtl);


